import Splide from "@splidejs/splide";
import "@splidejs/splide/css/core";

const PERSON_SLIDE_CLASSES = [
  "splide",
  "is-initialized",
  "is-rendered",
  "is-active",
  "is-overflow",
  "is-slider",
];

export function initTopNews() {
  const root = document.querySelector(".js-topNews");
  if (!root) return;

  const toggle = root.querySelector(".js-topNewsToggle");
  const label = root.querySelector(".js-topNewsToggleLabel");
  if (!toggle) return;

  const setOpen = (open) => {
    root.classList.toggle("is-open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (label) {
      label.textContent = open ? "お知らせを閉じる" : "お知らせを開く";
    }
  };

  toggle.addEventListener("click", () => {
    setOpen(!root.classList.contains("is-open"));
  });
}

function resetPersonSliderStyles(root) {
  root.querySelectorAll(".splide__slide").forEach((slide) => {
    slide.removeAttribute("style");
  });

  root.querySelector(".splide__list")?.removeAttribute("style");
  root.querySelector(".splide__track")?.removeAttribute("style");
}

export function initTopPersonCards() {
  const root = document.querySelector(".js-topPersonCards");
  if (!root) return;

  const mql = window.matchMedia("(max-width: 768px)");
  let splide = null;

  const mount = () => {
    if (splide || !mql.matches) return;

    root.classList.add("splide", "is-slider");

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    splide = new Splide(root, {
      type: "loop",
      focus: "center",
      fixedWidth: "326px",
      perPage: 1,
      gap: "16px",
      padding: { left: "12%", right: "12%" },
      pagination: true,
      arrows: false,
      drag: true,
      speed: reducedMotion ? 0 : 600,
      flickPower: 300,
    });

    splide.mount();
  };

  const destroy = () => {
    if (splide) {
      splide.destroy(true);
      splide = null;
    }

    root.classList.remove(...PERSON_SLIDE_CLASSES);
    resetPersonSliderStyles(root);
  };

  const update = () => {
    if (mql.matches) {
      mount();
    } else {
      destroy();
    }
  };

  mql.addEventListener("change", update);
  update();
}

export function initTopMvCatch() {
  const isSp = window.matchMedia("(max-width: 768px)").matches;
  const catchRoot = document.querySelector(
    isSp ? ".js-topMvCatch--sp" : ".js-topMvCatch--pc"
  );
  const pattern = document.querySelector(".js-topMvPattern");
  if (!catchRoot && !pattern) return;

  const chars = catchRoot?.querySelectorAll(".topMv__catchChar") ?? [];
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const show = () => {
    catchRoot?.classList.add("is-show");
    pattern?.classList.add("is-show");
  };

  if (reducedMotion) {
    show();
    return;
  }

  const timingEl = catchRoot ?? pattern;
  const styles = getComputedStyle(timingEl);
  const startDelaySec = parseFloat(
    styles.getPropertyValue("--mv-catch-char-start-delay")
  );
  const staggerMaxSec = parseFloat(
    styles.getPropertyValue("--mv-catch-char-stagger-max")
  );
  const startDelayMs = Number.isFinite(startDelaySec) ? startDelaySec * 1000 : 1950;
  const staggerMaxMs = Number.isFinite(staggerMaxSec) ? staggerMaxSec * 1000 : 1500;

  chars.forEach((char) => {
    char.style.transitionDelay = `${Math.random() * staggerMaxMs}ms`;
  });

  window.setTimeout(show, startDelayMs);
}

function getVisibleIntroBody() {
  const isSp = window.matchMedia("(max-width: 768px)").matches;
  return document.querySelector(
    isSp ? ".topIntro__body.pc-none" : ".topIntro__body.sp-none"
  );
}

const INTRO_TRIGGER_BOTTOM_RATIO = 0.25;

function getIntroTriggerZoneTop() {
  return window.innerHeight * (1 - INTRO_TRIGGER_BOTTOM_RATIO);
}

/** 段落の先頭行付近を基準にする（ブロック上端＋約1行分） */
function getIntroBlockAnchor(block) {
  const rect = block.getBoundingClientRect();
  const lineOffset = Math.min(36, Math.max(20, rect.height * 0.18));
  return rect.top + lineOffset;
}

function isIntroStylesReady(probe) {
  const bg = getComputedStyle(probe).backgroundImage;
  return Boolean(bg && bg !== "none");
}

function waitForIntroStyles(probe) {
  if (!probe) return Promise.resolve();

  return new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      if (isIntroStylesReady(probe) || attempts >= 120) {
        resolve();
        return;
      }
      attempts += 1;
      requestAnimationFrame(check);
    };
    check();
  });
}

function shouldActivateIntroBlock(block) {
  if (block.dataset.introTriggered === "true") return false;

  const rect = block.getBoundingClientRect();
  const zoneTop = getIntroTriggerZoneTop();
  const anchor = getIntroBlockAnchor(block);

  // 画面外（下）・（上）に完全に出ている場合は対象外
  if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;

  // 先頭行が下から 25% ゾーンに入った
  if (anchor >= zoneTop && anchor <= window.innerHeight) return true;

  // JS 初期化前に素早くスクロールしてトリガーを見逃した段落
  if (
    rect.bottom > 0 &&
    rect.bottom <= zoneTop &&
    rect.top < window.innerHeight * 0.85
  ) {
    return true;
  }

  return false;
}

export async function initTopIntroText() {
  const visibleBody = getVisibleIntroBody();
  const blocks = visibleBody?.querySelectorAll(".js-topIntroBlock") ?? [];
  if (!visibleBody || !blocks.length) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  if (reducedMotion) {
    visibleBody.querySelectorAll(".js-topIntroText").forEach((target) => {
      target.classList.add("is-active");
    });
    return;
  }

  await waitForIntroStyles(blocks[0].querySelector(".js-topIntroText"));

  const activateBlock = (block) => {
    if (block.dataset.introTriggered === "true") return;
    block.dataset.introTriggered = "true";

    const lines = block.querySelectorAll(".js-topIntroText");
    lines.forEach((line) => line.classList.remove("is-active"));
    void block.offsetWidth;

    requestAnimationFrame(() => {
      lines.forEach((line) => line.classList.add("is-active"));
    });
  };

  const checkBlocks = () => {
    blocks.forEach((block) => {
      if (shouldActivateIntroBlock(block)) activateBlock(block);
    });
  };

  checkBlocks();
  window.addEventListener("scroll", checkBlocks, { passive: true });
  window.addEventListener("resize", checkBlocks, { passive: true });
}

export function initTopPage() {
  initTopNews();
  initTopPersonCards();
  initTopMvCatch();
  void initTopIntroText();
  initYoutubeModal();
}

export function initYoutubeModal() {
  const modal = document.querySelector(".js-youtubeModal");
  const iframe = modal?.querySelector(".js-youtubeModalIframe");
  const openButtons = document.querySelectorAll(".js-youtubeModalOpen");
  if (!modal || !iframe || openButtons.length === 0) return;

  const closeButtons = modal.querySelectorAll(".js-youtubeModalClose");
  let lastFocus = null;

  const setOpen = (open, youtubeId, start) => {
    if (open) {
      if (!youtubeId) return;
      lastFocus = document.activeElement;
      let embedUrl = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;
      if (start) {
        embedUrl += `&start=${start}`;
      }
      iframe.src = embedUrl;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      modal.classList.add("is-open");
      document.body.classList.add("is-youtubeModalOpen");
      modal.querySelector(".youtubeModal__close")?.focus();
      return;
    }

    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal.hidden = true;
    document.body.classList.remove("is-youtubeModalOpen");
    iframe.src = "";
    if (lastFocus && typeof lastFocus.focus === "function") {
      lastFocus.focus();
    }
  };

  openButtons.forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      const start = btn.dataset.youtubeStart;
      setOpen(true, btn.dataset.youtubeId, start ? Number(start) : undefined);
    });
  });

  closeButtons.forEach((btn) => {
    btn.addEventListener("click", () => setOpen(false));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!modal.classList.contains("is-open")) return;
    setOpen(false);
  });
}
